import React from 'react';
import { FlatList, Platform } from 'react-native';
import styled from 'styled-components/native';
import Colors from '../constants/Colors';
import { B } from './Typography';

const StyledContainer = styled.View`
  width: 100%;
`;

const StyledItemContainer = styled.View`
  background-color: white;
  height: 164px;
  width: 311px;
  border-radius: 24px;
  border-color: #e2e5ea;
  border-width: 1px;
  overflow: hidden;
  ${Platform.OS === 'web' ? 'margin-bottom: 6px' : ''}
`;

const StyledTextContainer = styled.View`
  flex: 1;
  padding: 13px 13px 5px 13px;
`;

const StyledTitleContainer = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 4px 0 10px 0;
`;

const StyledTitleIcon = styled.Image`
  height: 22px;
  width: 22px;
`;

const StyledTitleText = styled.Text`
  font-family: 'DMSans-Bold';
  font-size: 12px;
  line-height: 15px;
  color: ${Colors.primary};
  margin-left: 7px;
`;

const StyledMessageText = styled(B)`
  flex: 1;
  color: ${Colors.primary};
`;

const StyleButtonItemContainer = styled.View`
  height: 53px;
  flex-direction: row;
  width: 100%;
`;

const StyledButtonContainer = styled.View`
  flex: 1;
  flex-direction: row;
`;

const StyledButton = styled.TouchableOpacity`
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: ${Colors.secondary};
`;

const StyledButtonText = styled.Text`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  padding: 0 6px;
  color: ${Colors.secondaryText};
`;

const StyledButtonsSeparator = styled.View`
  width: 1px;
`;

const StyledItemSeparator = styled.View`
  width: 12px;
`;

export interface Prompt {
  id: number;
  titleIconUrl?: string;
  title: string;
  message: string;
  buttonLabels: Array<string>;
}

interface PromptListProps {
  promptData: Array<Prompt>;
  disabled?: boolean;
  onSelect?: (prompt: Prompt, buttonLabel: string) => void;
}

const PromptList: React.FC<PromptListProps> = ({
  promptData,
  disabled,
  onSelect
}) => {
  return (
    <StyledContainer>
      <FlatList
        keyExtractor={(_, index) => index.toString()}
        horizontal
        overScrollMode='never'
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        data={promptData}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        ItemSeparatorComponent={() => <StyledItemSeparator />}
        renderItem={listItem => (
          <PromptListItem
            prompt={listItem.item}
            onSelect={onSelect}
            disabled={disabled}
          />
        )}
      />
    </StyledContainer>
  );
};

const PromptListItem: React.FC<{
  prompt: Prompt;
  disabled?: boolean;
  onSelect?: (prompt: Prompt, buttonLabel: string) => void;
}> = ({ prompt, disabled, onSelect }) => {
  return (
    <StyledItemContainer>
      <StyledTextContainer>
        <StyledTitleContainer>
          <StyledTitleIcon
            source={
              prompt.titleIconUrl
                ? { uri: prompt.titleIconUrl }
                : require('../assets/rewards/rewards-coin.png')
            }
          />
          <StyledTitleText>{prompt.title}</StyledTitleText>
        </StyledTitleContainer>
        <StyledMessageText>{prompt.message}</StyledMessageText>
      </StyledTextContainer>
      <StyleButtonItemContainer>
        {prompt.buttonLabels.map((buttonLabel, index) => (
          <StyledButtonContainer key={index}>
            {index > 0 && <StyledButtonsSeparator />}
            <StyledButton
              disabled={disabled}
              activeOpacity={0.7}
              onPress={() => onSelect?.(prompt, buttonLabel)}
            >
              <StyledButtonText numberOfLines={2}>
                {buttonLabel}
              </StyledButtonText>
            </StyledButton>
          </StyledButtonContainer>
        ))}
      </StyleButtonItemContainer>
    </StyledItemContainer>
  );
};

export default PromptList;
